import React, { useCallback, useState } from 'react';

import Button from '../../../../library/button/button';
import RangeSlider from '../../../../library/range-slider/range-slider';
import CategoryCollapsible from '../category-collapsible/category-collapsible';
import { getLimitedValue } from './price-range-category-utils';

import { getFilterId } from '../../../../shared/utils/filters/filters';

import './price-range-category.scss';

function isNewValueDifferent(proposedNewValue, value) {
  return (
    proposedNewValue?.[0] !== value?.[0] || proposedNewValue?.[1] !== value?.[1]
  );
}

interface PriceRangeCategoryProps {
  categoryCode?: string;
  categoryName?: string;
  defaultMaxPrice?: number;
  defaultMinPrice?: number;
  description?: string;
  highestPriceInResults?: number;
  isOpenByDefault?: boolean;
  lowestPriceInResults?: number;
  onApply: (value) => void;
  onSliderValueChange: (value) => void;
  toggleType?: string;
}

function PriceRangeCategory(props: PriceRangeCategoryProps) {
  const {
    categoryCode = '',
    categoryName = '',
    defaultMaxPrice,
    defaultMinPrice,
    description,
    highestPriceInResults,
    isOpenByDefault = false,
    lowestPriceInResults,
    onApply,
    onSliderValueChange,
    toggleType
  } = props;

  const type = 'slider';
  const buttonId = getFilterId({ categoryCode, type });
  const [value, setValue] = useState([
    defaultMinPrice || 0,
    defaultMaxPrice || 0
  ]);
  const [stateMin, stateMax] = value;

  function applyRange([min, max]: number[]) {
    onApply({
      categoryCode,
      categoryName,
      type,
      valueCode: `[${min} TO ${max}]`,
      valueName: `$${min} - $${max}`
    });
  }

  function applyChange() {
    applyRange(value);
    onSliderValueChange(value);
  }

  const onSliderChange = useCallback(
    newValues => {
      const proposedNewValue = getLimitedValue(
        newValues,
        lowestPriceInResults,
        highestPriceInResults,
        defaultMinPrice,
        defaultMaxPrice
      );
      if (isNewValueDifferent(proposedNewValue, value)) {
        setValue(proposedNewValue);
      }
    },
    [
      defaultMaxPrice,
      defaultMinPrice,
      highestPriceInResults,
      lowestPriceInResults,
      value
    ]
  );

  return (
    <CategoryCollapsible
      description={description}
      isOpenByDefault={isOpenByDefault}
      label={categoryName}
      toggleType={toggleType}
    >
      <div
        className="price-range-category__price-range-container"
        styleName="price-range-container"
      >
        <span styleName="price-range">
          ${stateMin}
          <span styleName="price-range-filter-to-label"> to </span> ${stateMax}
        </span>

        <div styleName="slider-container">
          <RangeSlider
            max={Math.max(highestPriceInResults || 0, 0)}
            min={Math.max(lowestPriceInResults || 0, 0)}
            onChange={onSliderChange}
            values={value}
          />
          <Button
            id={buttonId}
            onClick={applyChange}
            styleName="apply-range-button"
          >
            apply price range
          </Button>
        </div>
      </div>
    </CategoryCollapsible>
  );
}

export default PriceRangeCategory;
